import { z } from "zod";
import type { Operation } from "../types";

/**
 * Agent-settings fleet cluster — RO-2-11a / #44480.
 *
 * 31 `"use server"` actions across 9 files under `excellent/src/agents/**`,
 * converted to contracted Hono endpoints. None has a `route.ts` twin under
 * Next — they are form/server-action call sites only on the settings pages
 * (`/settings/agents/{clis,learning,autopilot,notify,inbound,fleet,mirror}`).
 * Live Next call sites stay on their server actions (`ccb89192`).
 *
 * Paths live under `/api/agents/*` because that is the subject plane these
 * settings govern (same plane as `/api/agents/activity`, worker spawn, action
 * verdicts). One contract file so the 9 source modules stay one mount surface
 * rather than nine new package-export subpaths.
 *
 * FormData signatures reshape to JSON with the same field names the forms
 * already submit. Dropped `revalidatePath` targets are listed at each mount
 * in the route file (L5).
 *
 * Accounting (must stay 31 = 7+6+6+4+3+2+1+1+1):
 *   cli-actions 7 · acp 6 · learning 6 · autopilot 4 · notify 3 ·
 *   inbound 2 · fleet 1 · mirror 1
 */

const okTrue = z.object({ ok: z.literal(true) });
const okBool = z.object({ ok: z.boolean() });
const idParams = z.object({ id: z.string().min(1) });

// ── CLI (src/agents/cli-actions.ts) ─────────────────────────────────────────

const linkResult = z.object({
  ok: z.boolean(),
  cliId: z.string(),
  reason: z.string().optional(),
  message: z.string(),
  configPath: z.string().optional(),
  restartRequired: z.boolean().optional(),
});
export type AgentCliLinkResult = z.infer<typeof linkResult>;

const customCliResult = z.object({
  ok: z.boolean(),
  id: z.string(),
  reason: z.string().optional(),
  message: z.string(),
});
export type AgentCliCustomResult = z.infer<typeof customCliResult>;

const installResult = z.object({
  ok: z.boolean(),
  installed: z.boolean(),
  output: z.string(),
  message: z.string(),
});
export type AgentCliInstallResult = z.infer<typeof installResult>;

const cliLinkOp: Operation<{ id: string }, void, void, AgentCliLinkResult> = {
  method: "POST",
  path: ({ id }) => `/api/agents/clis/${encodeURIComponent(id)}/link`,
  params: idParams,
  response: linkResult,
};

const cliUnlinkOp: Operation<{ id: string }, void, void, AgentCliLinkResult> = {
  method: "POST",
  path: ({ id }) => `/api/agents/clis/${encodeURIComponent(id)}/unlink`,
  params: idParams,
  response: linkResult,
};

const cliAuthorizeBody = z.object({ authorized: z.boolean() });
export type AgentCliAuthorizeBody = z.infer<typeof cliAuthorizeBody>;

const cliAuthorizeOp: Operation<{ id: string }, void, AgentCliAuthorizeBody, { ok: boolean }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/clis/${encodeURIComponent(id)}/authorize`,
  params: idParams,
  body: cliAuthorizeBody,
  response: okBool,
};

const mcpFormat = z.enum([
  "json-mcp-servers",
  "json-mcp-servers-transport",
  "toml-mcp-servers",
  "toml-mcp-array",
  "json-opencode",
  "json-amp-mcp-servers",
  "json-mcp-map",
  "json-context-servers",
  "yaml-mcp-servers",
  "yaml-goose-extensions",
]);
const customCliBody = z.object({
  id: z.string(),
  label: z.string(),
  vendor: z.string(),
  binaries: z.array(z.string()),
  mcpConfig: z.object({ path: z.string(), format: mcpFormat }).nullable(),
});
export type AgentCliCustomBody = z.infer<typeof customCliBody>;

const cliAddCustomOp: Operation<void, void, AgentCliCustomBody, AgentCliCustomResult> = {
  method: "POST",
  path: () => `/api/agents/clis/custom`,
  body: customCliBody,
  response: customCliResult,
};

const cliRemoveCustomOp: Operation<{ id: string }, void, void, { ok: boolean }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/clis/${encodeURIComponent(id)}/remove`,
  params: idParams,
  response: okBool,
};

const preferredBody = z.object({ id: z.string().nullable() });
export type AgentCliPreferredBody = z.infer<typeof preferredBody>;

const cliPreferredOp: Operation<void, void, AgentCliPreferredBody, { ok: boolean }> = {
  method: "POST",
  path: () => `/api/agents/clis/preferred`,
  body: preferredBody,
  response: okBool,
};

const cliInstallOp: Operation<{ id: string }, void, void, AgentCliInstallResult> = {
  method: "POST",
  path: ({ id }) => `/api/agents/clis/${encodeURIComponent(id)}/install`,
  params: idParams,
  response: installResult,
};

// ── ACP (src/agents/acp/actions.ts) ─────────────────────────────────────────

const acpSettingsBody = z.object({
  enabled: z.boolean(),
  defaultAgent: z.string(),
  chain: z.array(z.string()).optional(),
  thresholdPct: z.number().optional(),
  auto: z.boolean().optional(),
  fallbackEnabled: z.boolean().optional(),
  roles: z.record(z.string(), z.object({ agent: z.string() })).optional(),
  agentsDisabled: z.record(z.string(), z.boolean()).optional(),
});
export type AcpSettingsBody = z.infer<typeof acpSettingsBody>;

const acpSaveSettingsOp: Operation<void, void, AcpSettingsBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/acp/settings`,
  body: acpSettingsBody,
  response: okTrue,
};

const acpReseedBody = z.object({ force: z.boolean().optional() });
export type AcpReseedBody = z.infer<typeof acpReseedBody>;

const acpReseedOp: Operation<void, void, AcpReseedBody, { ok: boolean; reason: string }> = {
  method: "POST",
  path: () => `/api/agents/acp/reseed`,
  body: acpReseedBody,
  response: z.object({ ok: z.boolean(), reason: z.string() }),
};

const acpDisabledBody = z.object({ disabled: z.boolean() });
export type AcpDisabledBody = z.infer<typeof acpDisabledBody>;

const acpSetDisabledOp: Operation<{ id: string }, void, AcpDisabledBody, { ok: boolean }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/acp/agents/${encodeURIComponent(id)}/disabled`,
  params: idParams,
  body: acpDisabledBody,
  response: okBool,
};

const acpDoctorResult = z.object({
  ok: z.boolean(),
  agentId: z.string(),
  installed: z.boolean(),
  resolved: z.boolean(),
  initializeOk: z.boolean().nullable(),
  billing: z.string().nullable(),
  message: z.string(),
});
export type AcpDoctorResult = z.infer<typeof acpDoctorResult>;

/** True read — probe only; no agents.json write (inventory QUERY was correct). */
const acpDoctorOp: Operation<{ id: string }, void, void, AcpDoctorResult> = {
  method: "GET",
  path: ({ id }) => `/api/agents/acp/agents/${encodeURIComponent(id)}/doctor`,
  params: idParams,
  response: acpDoctorResult,
};

const acpSeedPreview = z.object({
  defaultAgent: z.string(),
  chain: z.array(z.string()),
  reason: z.string(),
  needsInstallGuide: z.boolean(),
});
export type AcpSeedPreview = z.infer<typeof acpSeedPreview>;

const acpPreviewSeedOp: Operation<void, void, void, AcpSeedPreview> = {
  method: "GET",
  path: () => `/api/agents/acp/seed/preview`,
  response: acpSeedPreview,
};

const acpAgentView = z.object({
  id: z.string(),
  label: z.string(),
  installed: z.boolean(),
  authed: z.boolean(),
  authorized: z.boolean(),
  disabled: z.boolean(),
  billing: z.string(),
  binaryPath: z.string().nullable(),
  experimental: z.boolean(),
});

const acpListAgentsOp: Operation<void, void, void, { agents: z.infer<typeof acpAgentView>[] }> = {
  method: "GET",
  path: () => `/api/agents/acp/agents`,
  response: z.object({ agents: z.array(acpAgentView) }),
};

// ── Learning (src/agents/learning/actions.ts) ───────────────────────────────

const learningSettingsBody = z.object({
  enabled: z.boolean(),
  autoSkillTasks: z.boolean(),
  injectTopK: z.number().optional(),
});
export type LearningSettingsBody = z.infer<typeof learningSettingsBody>;

const learningSaveSettingsOp: Operation<void, void, LearningSettingsBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/learning/settings`,
  body: learningSettingsBody,
  response: okTrue,
};

const learningIdParams = z.object({ id: z.coerce.number().int().positive() });

const learningApproveOp: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/learning/${id}/approve`,
  params: learningIdParams,
  response: okTrue,
};

const learningRejectOp: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/learning/${id}/reject`,
  params: learningIdParams,
  response: okTrue,
};

const learningDismissOp: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/learning/${id}/dismiss`,
  params: learningIdParams,
  response: okTrue,
};

const learningPinBody = z.object({ pinned: z.boolean() });
export type LearningPinBody = z.infer<typeof learningPinBody>;

const learningPinOp: Operation<{ id: number }, void, LearningPinBody, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/agents/learning/${id}/pin`,
  params: learningIdParams,
  body: learningPinBody,
  response: okTrue,
};

const learningAddBody = z.object({ text: z.string() });
export type LearningAddBody = z.infer<typeof learningAddBody>;

const learningAddOp: Operation<void, void, LearningAddBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/learning`,
  body: learningAddBody,
  response: okTrue,
};

// ── Autopilot (src/agents/autopilot/actions.ts) ─────────────────────────────

const roleAutopilot = z.object({
  enabled: z.boolean(),
  /** Cadence in minutes (form unit); handler converts to seconds. */
  cadenceMinutes: z.number(),
});
const autopilotConfigBody = z.object({
  enabled: z.boolean(),
  roles: z.record(z.string(), roleAutopilot),
});
export type AutopilotConfigBody = z.infer<typeof autopilotConfigBody>;

const autopilotSaveConfigOp: Operation<void, void, AutopilotConfigBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/autopilot/config`,
  body: autopilotConfigBody,
  response: okTrue,
};

const autopilotApplyProfileOp: Operation<void, void, void, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/autopilot/apply-review-profile`,
  response: okTrue,
};

const goalReconcileBody = z.object({ enabled: z.boolean() });
export type GoalReconcileBody = z.infer<typeof goalReconcileBody>;

const autopilotGoalReconcileOp: Operation<void, void, GoalReconcileBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/autopilot/goal-reconcile`,
  body: goalReconcileBody,
  response: okTrue,
};

const autopilotDisableOp: Operation<void, void, void, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/autopilot/disable`,
  response: okTrue,
};

// ── Notify (src/agents/notify/actions.ts) ───────────────────────────────────

const notifyEventKeys = [
  "reviewQueue",
  "promoteReady",
  "autopilotStall",
  "auditHigh",
  "inboundAck",
] as const;

const notifySettingsBody = z.object({
  enabled: z.boolean(),
  provider: z.enum(["log", "resend", "telegram", "http"]),
  from: z.string().optional(),
  events: z.object({
    reviewQueue: z.boolean(),
    promoteReady: z.boolean(),
    autopilotStall: z.boolean(),
    auditHigh: z.boolean(),
    inboundAck: z.boolean(),
  }),
  digest: z.enum(["instant", "daily"]),
  quietHoursStart: z.number().optional(),
  quietHoursEnd: z.number().optional(),
});
export type NotifySettingsBody = z.infer<typeof notifySettingsBody>;
export const NOTIFY_EVENT_KEYS = notifyEventKeys;

const notifySaveSettingsOp: Operation<void, void, NotifySettingsBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/notify/settings`,
  body: notifySettingsBody,
  response: okTrue,
};

const notifyRecipientBody = z.object({ value: z.string() });
export type NotifyRecipientBody = z.infer<typeof notifyRecipientBody>;

const notifySaveRecipientOp: Operation<void, void, NotifyRecipientBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/notify/recipient`,
  body: notifyRecipientBody,
  response: okTrue,
};

const notifyTestResult = z.object({
  ok: z.boolean(),
  provider: z.string(),
  status: z.string(),
  error: z.string().optional(),
});
export type NotifyTestResult = z.infer<typeof notifyTestResult>;

const notifyTestOp: Operation<void, void, void, NotifyTestResult> = {
  method: "POST",
  path: () => `/api/agents/notify/test`,
  response: notifyTestResult,
};

// ── Inbound (src/agents/inbound/actions.ts) ─────────────────────────────────

const inboundSettingsBody = z.object({
  enabled: z.boolean(),
  rateLimitPerHour: z.number().optional(),
});
export type InboundSettingsBody = z.infer<typeof inboundSettingsBody>;

const inboundSaveSettingsOp: Operation<void, void, InboundSettingsBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/inbound/settings`,
  body: inboundSettingsBody,
  response: okTrue,
};

const inboundPairingResult = z.object({
  code: z.string(),
  expiresAt: z.string(),
  ttlSeconds: z.number(),
});
export type InboundPairingResult = z.infer<typeof inboundPairingResult>;

const inboundMintPairingOp: Operation<void, void, void, InboundPairingResult> = {
  method: "POST",
  path: () => `/api/agents/inbound/pairing-code`,
  response: inboundPairingResult,
};

// ── Fleet / Mirror ─────────────────────────────────────────────────────────

const fleetSettingsBody = z.object({
  enabled: z.boolean(),
  poolSize: z.number().optional(),
});
export type FleetSettingsBody = z.infer<typeof fleetSettingsBody>;

const fleetSaveSettingsOp: Operation<void, void, FleetSettingsBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/fleet/settings`,
  body: fleetSettingsBody,
  response: okTrue,
};

const mirrorSettingsBody = z.object({
  enabled: z.boolean(),
  remote: z.string().optional(),
  branch: z.string().optional(),
  intervalSeconds: z.number().optional(),
});
export type MirrorSettingsBody = z.infer<typeof mirrorSettingsBody>;

const mirrorSaveSettingsOp: Operation<void, void, MirrorSettingsBody, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/mirror/settings`,
  body: mirrorSettingsBody,
  response: okTrue,
};

// ── Barrel ──────────────────────────────────────────────────────────────────

export const agentSettings = {
  // CLI 7
  cliLink: cliLinkOp,
  cliUnlink: cliUnlinkOp,
  cliAuthorize: cliAuthorizeOp,
  cliAddCustom: cliAddCustomOp,
  cliRemoveCustom: cliRemoveCustomOp,
  cliPreferred: cliPreferredOp,
  cliInstall: cliInstallOp,
  // ACP 6
  acpSaveSettings: acpSaveSettingsOp,
  acpReseed: acpReseedOp,
  acpSetDisabled: acpSetDisabledOp,
  acpDoctor: acpDoctorOp,
  acpPreviewSeed: acpPreviewSeedOp,
  acpListAgents: acpListAgentsOp,
  // Learning 6
  learningSaveSettings: learningSaveSettingsOp,
  learningApprove: learningApproveOp,
  learningReject: learningRejectOp,
  learningDismiss: learningDismissOp,
  learningPin: learningPinOp,
  learningAdd: learningAddOp,
  // Autopilot 4
  autopilotSaveConfig: autopilotSaveConfigOp,
  autopilotApplyProfile: autopilotApplyProfileOp,
  autopilotGoalReconcile: autopilotGoalReconcileOp,
  autopilotDisable: autopilotDisableOp,
  // Notify 3
  notifySaveSettings: notifySaveSettingsOp,
  notifySaveRecipient: notifySaveRecipientOp,
  notifyTest: notifyTestOp,
  // Inbound 2
  inboundSaveSettings: inboundSaveSettingsOp,
  inboundMintPairing: inboundMintPairingOp,
  // Singles 2
  fleetSaveSettings: fleetSaveSettingsOp,
  mirrorSaveSettings: mirrorSaveSettingsOp,
};

/** Flat list for bijection checks. */
export const AGENT_SETTINGS_OPS = Object.values(agentSettings);
